
// src/utils/attendanceFormat.js
// IST-safe date / time helpers shared by the Attendance and Visit Report pages.

const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;

export const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const WEEKDAY_FULL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTHS_FULL = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const pad = (n) => String(n).padStart(2, '0');

// "YYYY-MM-DD" in IST for any date (default: now)
export function formatDateKey(date = new Date()) {
  const d = new Date(new Date(date).getTime() + IST_OFFSET_MS);
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}

// Add / subtract days from a "YYYY-MM-DD" key
export function shiftDateKey(key, days) {
  const [y, m, d] = key.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d + days));
  return `${dt.getUTCFullYear()}-${pad(dt.getUTCMonth() + 1)}-${pad(dt.getUTCDate())}`;
}

export function addMonths(month, delta) {
  const [y, m] = month.split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 1 + delta, 1));
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}`;
}

// "08:20 PM" (IST)
export function formatISTTime(date) {
  if (!date) return '—';
  const d = new Date(new Date(date).getTime() + IST_OFFSET_MS);
  let h = d.getUTCHours();
  const m = pad(d.getUTCMinutes());
  const ampm = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  return `${pad(h)}:${m} ${ampm}`;
}

// "08 Oct 2026"
export function formatHumanDate(key) {
  if (!key) return '—';
  const [y, m, d] = key.split('-').map(Number);
  return `${pad(d)} ${MONTHS[m - 1]} ${y}`;
}

// "Thu · 08 Oct 2026"
export function formatDayLabel(key) {
  if (!key) return '—';
  const [y, m, d] = key.split('-').map(Number);
  const wd = WEEKDAYS[new Date(Date.UTC(y, m - 1, d)).getUTCDay()];
  return `${wd} · ${formatHumanDate(key)}`;
}

// "08 Oct 2026 (Thursday)"
export function formatFullDate(key) {
  if (!key) return '—';
  const [y, m, d] = key.split('-').map(Number);
  const wd = WEEKDAY_FULL[new Date(Date.UTC(y, m - 1, d)).getUTCDay()];
  return `${formatHumanDate(key)} (${wd})`;
}

// "October 2026"
export function monthLabel(month) {
  if (!month) return '—';
  const [y, m] = month.split('-').map(Number);
  return `${MONTHS_FULL[m - 1]} ${y}`;
}

// "8h 05m" / "37m"
export function formatDuration(minutes) {
  const total = Math.max(0, Math.floor(Number(minutes) || 0));
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (h === 0) return `${m}m`;
  return `${h}h ${pad(m)}m`;
}

// "08 Oct 2026 · 04:01 PM" for an ISO timestamp
export function formatDateTime(iso) {
  if (!iso) return '—';
  return `${formatHumanDate(formatDateKey(iso))} · ${formatISTTime(iso)}`;
}

// 1 → "1st", 2 → "2nd", 3 → "3rd" …
export function ordinal(n) {
  const v = Number(n) || 0;
  const s = ['th', 'st', 'nd', 'rd'];
  const m = v % 100;
  return v + (s[(m - 20) % 10] || s[m] || s[0]);
}

// ── Attendance day types ─────────────────────────────────────────
// There is no "late" concept: a day is Full Day (PRESENT) or Half Day,
// decided by hours worked. Old records saved as LATE are shown as Full Day.
export const normalizeStatus = (status) => (status === 'LATE' ? 'PRESENT' : status);

export const STATUS_META = {
  PRESENT:  { label: 'Full Day', letter: 'F',  color: '#10b981', bg: 'rgba(16,185,129,0.14)' },
  HALF_DAY: { label: 'Half Day', letter: 'HD', color: '#3b82f6', bg: 'rgba(59,130,246,0.14)' },
  ABSENT:   { label: 'Absent',   letter: 'A',  color: '#ef4444', bg: 'rgba(239,68,68,0.14)' },
  ON_LEAVE: { label: 'On Leave', letter: 'OL', color: '#8b5cf6', bg: 'rgba(139,92,246,0.14)' },
  HOLIDAY:  { label: 'Holiday',  letter: 'H',  color: '#6366f1', bg: 'rgba(99,102,241,0.14)' },
  WEEK_OFF: { label: 'Week Off', letter: 'W',  color: '#64748b', bg: 'rgba(100,116,139,0.16)' },
};

export const statusMeta = (status) => STATUS_META[normalizeStatus(status)] || STATUS_META.ABSENT;

// ── Lead status (visit reports) ──────────────────────────────────
export const FLOW_META = {
  COLD: { label: 'Cold', color: '#3b82f6', bg: 'rgba(59,130,246,0.14)', hint: 'Not interested' },
  WARM: { label: 'Warm', color: '#f59e0b', bg: 'rgba(245,158,11,0.16)', hint: 'Follow up' },
  HOT:  { label: 'Hot',  color: '#ef4444', bg: 'rgba(239,68,68,0.14)',  hint: 'Ready to buy' },
};

// ── Date-range presets used by both dashboards ───────────────────
export const RANGE_PRESETS = [
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'week', label: '7 Days' },
  { key: 'month', label: 'This Month' },
  { key: 'custom', label: 'Custom' },
];

export function computeRange(key, customFrom, customTo) {
  const today = formatDateKey();
  switch (key) {
    case 'today':
      return { from: today, to: today };
    case 'yesterday': {
      const y = shiftDateKey(today, -1);
      return { from: y, to: y };
    }
    case 'week':
      return { from: shiftDateKey(today, -6), to: today };
    case 'month':
      return { from: `${today.slice(0, 7)}-01`, to: today };
    case 'all':
      return {};
    case 'custom':
    default: {
      const from = customFrom || today;
      const to = customTo || from;
      return from <= to ? { from, to } : { from: to, to: from };
    }
  }
}


//--------- 13.10.2026 Backup --------------
// // src/utils/attendanceFormat.js
// // IST-safe date / time helpers shared by the Attendance and Visit Report pages.

// const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;

// export const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
// const WEEKDAY_FULL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
// const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
// const MONTHS_FULL = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

// const pad = (n) => String(n).padStart(2, '0');

// // "YYYY-MM-DD" in IST for any date (default: now)
// export function formatDateKey(date = new Date()) {
//   const d = new Date(new Date(date).getTime() + IST_OFFSET_MS);
//   return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
// }

// // Add / subtract days from a "YYYY-MM-DD" key
// export function shiftDateKey(key, days) {
//   const [y, m, d] = key.split('-').map(Number);
//   const dt = new Date(Date.UTC(y, m - 1, d + days));
//   return `${dt.getUTCFullYear()}-${pad(dt.getUTCMonth() + 1)}-${pad(dt.getUTCDate())}`;
// }

// export function addMonths(month, delta) {
//   const [y, m] = month.split('-').map(Number);
//   const d = new Date(Date.UTC(y, m - 1 + delta, 1));
//   return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}`;
// }

// // "08:20 PM" (IST)
// export function formatISTTime(date) {
//   if (!date) return '—';
//   const d = new Date(new Date(date).getTime() + IST_OFFSET_MS);
//   let h = d.getUTCHours();
//   const m = pad(d.getUTCMinutes());
//   const ampm = h >= 12 ? 'PM' : 'AM';
//   h = h % 12 || 12;
//   return `${pad(h)}:${m} ${ampm}`;
// }

// // "08 Oct 2026"
// export function formatHumanDate(key) {
//   if (!key) return '—';
//   const [y, m, d] = key.split('-').map(Number);
//   return `${pad(d)} ${MONTHS[m - 1]} ${y}`;
// }

// // "Thu · 08 Oct 2026"
// export function formatDayLabel(key) {
//   if (!key) return '—';
//   const [y, m, d] = key.split('-').map(Number);
//   const wd = WEEKDAYS[new Date(Date.UTC(y, m - 1, d)).getUTCDay()];
//   return `${wd} · ${formatHumanDate(key)}`;
// }

// // "08 Oct 2026 (Thursday)"
// export function formatFullDate(key) {
//   if (!key) return '—';
//   const [y, m, d] = key.split('-').map(Number);
//   const wd = WEEKDAY_FULL[new Date(Date.UTC(y, m - 1, d)).getUTCDay()];
//   return `${formatHumanDate(key)} (${wd})`;
// }

// // "October 2026"
// export function monthLabel(month) {
//   if (!month) return '—';
//   const [y, m] = month.split('-').map(Number);
//   return `${MONTHS_FULL[m - 1]} ${y}`;
// }

// // "8h 05m" / "37m"
// export function formatDuration(minutes) {
//   const total = Math.max(0, Math.floor(Number(minutes) || 0));
//   const h = Math.floor(total / 60);
//   const m = total % 60;
//   if (h === 0) return `${m}m`;
//   return `${h}h ${pad(m)}m`;
// }

// // "08 Oct 2026 · 04:01 PM" for an ISO timestamp
// export function formatDateTime(iso) {
//   if (!iso) return '—';
//   return `${formatHumanDate(formatDateKey(iso))} · ${formatISTTime(iso)}`;
// }

// // 1 → "1st", 2 → "2nd", 3 → "3rd" …
// export function ordinal(n) {
//   const v = Number(n) || 0;
//   const s = ['th', 'st', 'nd', 'rd'];
//   const m = v % 100;
//   return v + (s[(m - 20) % 10] || s[m] || s[0]);
// }

// // ── Attendance day types ─────────────────────────────────────────
// // There is no "late" concept: a day is Full Day (PRESENT) or Half Day,
// // decided by hours worked. Old records saved as LATE are shown as Full Day.
// export const normalizeStatus = (status) => (status === 'LATE' ? 'PRESENT' : status);

// export const STATUS_META = {
//   PRESENT:  { label: 'Full Day', letter: 'F',  color: '#10b981', bg: 'rgba(16,185,129,0.14)' },
//   HALF_DAY: { label: 'Half Day', letter: 'HD', color: '#3b82f6', bg: 'rgba(59,130,246,0.14)' },
//   ABSENT:   { label: 'Absent',   letter: 'A',  color: '#ef4444', bg: 'rgba(239,68,68,0.14)' },
//   ON_LEAVE: { label: 'On Leave', letter: 'OL', color: '#8b5cf6', bg: 'rgba(139,92,246,0.14)' },
//   HOLIDAY:  { label: 'Holiday',  letter: 'H',  color: '#6366f1', bg: 'rgba(99,102,241,0.14)' },
//   WEEK_OFF: { label: 'Week Off', letter: 'W',  color: '#64748b', bg: 'rgba(100,116,139,0.16)' },
// };

// export const statusMeta = (status) => STATUS_META[normalizeStatus(status)] || STATUS_META.ABSENT;

// // ── Lead status (visit reports) ──────────────────────────────────
// export const FLOW_META = {
//   COLD: { label: 'Cold', color: '#3b82f6', bg: 'rgba(59,130,246,0.14)', hint: 'Not interested' },
//   WARM: { label: 'Warm', color: '#f59e0b', bg: 'rgba(245,158,11,0.16)', hint: 'Follow up' },
//   HOT:  { label: 'Hot',  color: '#ef4444', bg: 'rgba(239,68,68,0.14)',  hint: 'Ready to buy' },
// };

// // ── Date-range presets used by both dashboards ───────────────────
// export const RANGE_PRESETS = [
//   { key: 'today', label: 'Today' },
//   { key: 'yesterday', label: 'Yesterday' },
//   { key: 'week', label: '7 Days' },
//   { key: 'month', label: 'This Month' },
//   { key: 'custom', label: 'Custom' },
// ];

// export function computeRange(key, customFrom, customTo) {
//   const today = formatDateKey();
//   switch (key) {
//     case 'today':
//       return { from: today, to: today };
//     case 'yesterday': {
//       const y = shiftDateKey(today, -1);
//       return { from: y, to: y };
//     }
//     case 'week':
//       return { from: shiftDateKey(today, -6), to: today };
//     case 'month':
//       return { from: `${today.slice(0, 7)}-01`, to: today };
//     case 'all':
//       return {};
//     case 'custom':
//     default: {
//       const from = customFrom || today;
//       const to = customTo || from;
//       return from <= to ? { from, to } : { from: to, to: from };
//     }
//   }
// }
